import { ScenarioMetadata } from "@api/apiClient";
import i18n from "@i18n/i18n";
import { Card, CardContent, CardHeader, Grid, Typography, useTheme } from "@mui/material";
import TextField from "@mui/material/TextField";
import { ChangeEvent } from "react";

interface ScenarioAdditionalMetadataFormProps {
  metadata: ScenarioMetadata;
  onChange: (name: string, value: any) => void;
}

export const ScenarioMetadataForm = ({ metadata, onChange }: ScenarioAdditionalMetadataFormProps) => {
  const theme = useTheme();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;

    // Handle different input types
    let processedValue: number | undefined;

    if (value === "" || value === null || value === undefined) {
      processedValue = undefined;
    } else {
      const numValue = parseFloat(value);
      // Check if the value is a valid number and not NaN
      if (!isNaN(numValue) && numValue >= 0) {
        if (name === "totalFloorArea") {
          // Accept decimal values for total floor area
          processedValue = numValue;
        } else if (name === "serviceLife") {
          // Only accept natural numbers (integers) for service life
          processedValue = Number.isInteger(numValue) ? numValue : Math.floor(numValue);
        }
      } else {
        // For invalid numbers, keep the current value
        const currentValue = metadata[name as keyof ScenarioMetadata];
        processedValue = typeof currentValue === "number" ? currentValue : undefined;
      }
    }

    onChange(name, processedValue);
  };

  return (
    <Card elevation={3} sx={{ "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default } }}>
      <CardHeader
        title={
          <Typography variant="h6" fontWeight="bold">
            {i18n.t("dashboard.title.additional-metadata")}
          </Typography>
        }
        elevation={3}
      />
      <CardContent sx={{ display: "flex", alignItems: "center", borderTop: 1, borderColor: theme.palette.divider }}>
        <Grid container spacing={2} sx={{ width: "100%" }}>
          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label={i18n.t("dashboard.title.total-floor-area")}
                  name="totalFloorArea"
                  type="number"
                  value={metadata.totalFloorArea || ""}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    inputProps: {
                      min: 0,
                      step: "any", // Allow decimal values
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label={i18n.t("dashboard.title.service-life")}
                  name="serviceLife"
                  type="number"
                  value={metadata.serviceLife || ""}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    inputProps: {
                      min: 0,
                      step: 1, // Only integer values
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
